import { getAnalytics } from 'src/lib/analytics/analytics.service';
import type { tPerfReloadCause } from 'src/hooks/analytics/perf/perfAnalytics.constants';
import { getPerfArms, type tPerfReplayArm, type tPerfStructuralArm } from './perfArms';
import { getPerfInteractionCount } from './perfCounters.store';
import { PERF_REPLAY_ARMS, PERF_STRUCTURAL_ARMS } from './perfArms.constants';

export type tReloadEvidence = {
  wasDiscarded: boolean;
  activationStart: number;
  navigationType: string | undefined;
  previousChunkFailure: boolean;
  appReloadAt: number | null;
  nowMs: number;
};
type tExitState = 'visible' | 'hidden' | 'pagehide';
type tDocumentSummary = {
  ageMs: number;
  interactionsTotal: number | null;
  replayArm: tPerfReplayArm;
  s1: tPerfStructuralArm;
  s2: tPerfStructuralArm;
  exitState: tExitState;
  chunkFailure: boolean;
};
const SUMMARY_KEY = 'leadsales_perf_previous_document_v1';
const APP_RELOAD_KEY = 'leadsales_perf_app_reload_at_v1';
let onSuccessfulRoute: (() => void) | undefined;

export const classifyReload = (evidence: tReloadEvidence): tPerfReloadCause => {
  if (evidence.wasDiscarded) return 'restore';
  if (evidence.activationStart > 0) return 'prerender';
  if (evidence.navigationType === 'navigate') return 'navigation';
  if (evidence.navigationType === 'back_forward') return 'restore';
  if (evidence.navigationType !== 'reload') return 'unknown';
  if (evidence.previousChunkFailure) return 'after_chunk_failure';
  const age = evidence.appReloadAt === null ? Infinity : evidence.nowMs - evidence.appReloadAt;
  return age >= 0 && age < 10_000 ? 'app_reload' : 'user_f5';
};

const storageKey = (key: string): string => `${key}:${getPerfArms().tabId}`;
const takeSession = (key: string): string | null => {
  try {
    const scoped = storageKey(key);
    const value = sessionStorage.getItem(scoped);
    sessionStorage.removeItem(scoped);
    return value;
  } catch { return null; }
};
const safeNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER;
const parseSummary = (raw: string | null): tDocumentSummary | undefined => {
  try {
    const value: unknown = raw ? JSON.parse(raw) : undefined;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return;
    const row = value as Record<string, unknown>;
    if (row.version !== 1 || !safeNumber(row.ageMs) ||
        (row.interactionsTotal !== null && !(safeNumber(row.interactionsTotal) && Number.isSafeInteger(row.interactionsTotal))) ||
        !PERF_REPLAY_ARMS.some(arm => arm === row.replayArm) ||
        !PERF_STRUCTURAL_ARMS.some(arm => arm === row.s1) || !PERF_STRUCTURAL_ARMS.some(arm => arm === row.s2) ||
        !['visible', 'hidden', 'pagehide'].some(state => state === row.exitState) || typeof row.chunkFailure !== 'boolean') return;
    return { ageMs: row.ageMs, interactionsTotal: row.interactionsTotal as number | null,
      replayArm: row.replayArm as tPerfReplayArm, s1: row.s1 as tPerfStructuralArm,
      s2: row.s2 as tPerfStructuralArm, exitState: row.exitState as tExitState, chunkFailure: row.chunkFailure };
  } catch { return undefined; }
};
const navigation = (): (PerformanceNavigationTiming & { activationStart?: number }) | undefined => {
  try { return performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined; }
  catch { return undefined; }
};

export const markAppReload = (): void => {
  try { sessionStorage.setItem(storageKey(APP_RELOAD_KEY), String(Date.now())); } catch { /* Diagnostic only. */ }
};
export const markRouteRendered = (): void => { onSuccessfulRoute?.(); };

const sessionProperties = (cause: tPerfReloadCause, previous?: tDocumentSummary) => ({
  reload_cause: cause,
  perf_previous_document_age_ms: previous?.ageMs ?? null,
  perf_previous_document_interactions_total: previous?.interactionsTotal ?? null,
  perf_previous_document_replay_arm: previous?.replayArm ?? null,
  perf_previous_document_s1: previous?.s1 ?? null,
  perf_previous_document_s2: previous?.s2 ?? null,
  perf_previous_document_exit_state: previous?.exitState ?? null,
  perf_previous_document_chunk_failure: previous?.chunkFailure ?? null,
});
const readBootProperties = () => {
  const previous = parseSummary(takeSession(SUMMARY_KEY));
  const marker = takeSession(APP_RELOAD_KEY);
  const nav = navigation();
  let discarded = false;
  try { discarded = (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true; } catch { /* Unsupported getter. */ }
  const cause = classifyReload({ wasDiscarded: discarded, activationStart: nav?.activationStart ?? 0,
    navigationType: nav?.type, previousChunkFailure: previous?.chunkFailure ?? false,
    appReloadAt: marker !== null && safeNumber(Number(marker)) ? Number(marker) : null, nowMs: Date.now() });
  return sessionProperties(cause, previous);
};

export const startReloadClassifier = (): (() => void) => {
  let active = false;
  let chunkFailure = false;
  let properties = sessionProperties('prerender');
  const register = (): void => {
    try { getAnalytics().registerForSession(properties); } catch { /* Telemetry cannot block boot. */ }
  };
  const persist = (state?: tExitState): void => {
    if (!active) return;
    try {
      const arms = getPerfArms();
      const ageMs = Math.max(0, performance.now() - (navigation()?.activationStart ?? 0));
      if (!safeNumber(ageMs)) return;
      const count = getPerfInteractionCount();
      const summary: tDocumentSummary = {
        ageMs, interactionsTotal: safeNumber(count) && Number.isSafeInteger(count) ? count : null,
        replayArm: arms.replayArm, s1: arms.effective.perf_s1, s2: arms.effective.perf_s2,
        exitState: state ?? (document.visibilityState === 'hidden' ? 'hidden' : 'visible'), chunkFailure,
      };
      sessionStorage.setItem(storageKey(SUMMARY_KEY), JSON.stringify({ version: 1, ...summary }));
    } catch { /* Exit metadata must not interrupt navigation. */ }
  };
  const onChunkFailure = (): void => { chunkFailure = true; persist(); };
  const onHidden = (): void => { if (document.visibilityState === 'hidden') persist('hidden'); };
  const onPageHide = (): void => { persist('pagehide'); };
  const rendered = (): void => { if (chunkFailure) { chunkFailure = false; persist(); } };
  const activate = (): void => {
    if (active) return;
    active = true;
    properties = readBootProperties();
    register();
    if (chunkFailure) persist();
  };
  onSuccessfulRoute = rendered;
  window.addEventListener('vite:preloadError', onChunkFailure);
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onPageHide);
  let prerendering = false;
  try { prerendering = (document as Document & { prerendering?: boolean }).prerendering === true; } catch { /* Unsupported getter. */ }
  if (prerendering) {
    register();
    document.addEventListener('prerenderingchange', activate, { once: true });
  } else activate();
  let stopSession = () => {};
  try { stopSession = getAnalytics().onSessionId(register); } catch { /* Disabled analytics. */ }
  return () => {
    stopSession();
    if (onSuccessfulRoute === rendered) onSuccessfulRoute = undefined;
    window.removeEventListener('vite:preloadError', onChunkFailure);
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', onPageHide);
    document.removeEventListener('prerenderingchange', activate);
  };
};
